<template>
  <div>
    <vue-headful :title="this.setPageTitle('scoringProfilesModelsList')" />
    <loading :active="getLoading" :color="'blue'" is-full-page loader="spinner"> </loading>
    <b-container class="my-4">
      <!-- HEADER -->
      <custom-flex-header :parent-node-class="['my-4']">
        <template v-slot:left-content>
          <h1 class="mb-0 h4 font-weight-normal">
            {{ $t('scoringProfilesModelsList.scoringProfilesModelsTitle') }}
          </h1>
        </template>
        <template v-slot:right-content>
          <b-button size="sm" variant="primary" @click="goToNewModel">
            {{ $t('scoringProfilesModelsList.goToNewModel') }}
          </b-button>
        </template>
      </custom-flex-header>

      <!-- TABLE FILTER -->
      <b-row class="mb-4">
        <b-col>
          <table-filters>
            <template v-slot:search>
              <label class="fieldLabel" for="name" v-t="'scoringProfilesModelsList.modelsListTable.filterText'"></label>
              <custom-search-bar
                :debounce="800"
                :text-value="getSearchBarValue"
                :placeholder="$t('scoringProfilesModelsList.modelsListTable.searchBarPlaceholder')"
                @on-search="onSearchModel"
              ></custom-search-bar>
            </template>
            <template v-slot:filteredItems>
              <span class="text-secondary align-middle">
                <span class="font-weight-bold">{{ getPagination.items }}</span>
                {{ $tc('scoringProfilesModelsList.modelsListTable.filteredModels', getPagination.items) }}
              </span>
            </template>
            <template v-slot:resetButton>
              <div>
                <b-button @click="onResetFilters" variant="link" :disabled="!hasSearched" class="reset-button">
                  <span>{{ $t('search.label.resetFilters') }} <i class="fa fa-trash small ml-1 icon-size-10"></i></span>
                </b-button>
              </div>
            </template>
          </table-filters>
        </b-col>
      </b-row>

      <template v-if="getModelsList.length > 0">
        <b-row class="mb-4">
          <b-col>
            <div class="ml-auto">
              <table-pagination
                :pagination="getPagination"
                :pagination-options="pagination_options"
                :pagination-text="getPaginationItemsCounter"
                :products-per-page="$t('scoringProfilesModelsList.perPage')"
                :pagination-class="['mb-4']"
                @on-change-per-page="onChangePerPage"
                @on-change-current-page="onChangeCurrentPage"
              >
              </table-pagination>
            </div>
          </b-col>
        </b-row>

        <!-- TABLE SCORING PROFILES MODELS -->
        <b-row class="mb-4">
          <b-col>
            <b-table
              class="list-table models-list-table mb-0"
              hover
              striped
              no-local-sorting
              no-sort-reset
              :fields="fields"
              :tbody-tr-class="['']"
              :items="getModelsList"
              @sort-changed="onSortingChanged"
            >
              <template v-slot:cell(actions)="{ item }">
                <!-- actions -->
                <div class="text-right">
                  <b-dropdown size="md" toggle-class="text-decoration-none button-primary-class p-3" right no-caret>
                    <template #button-content>
                      <i class="fas fa-ellipsis-v"></i>
                      <span class="sr-only">{{ $t('scoringProfilesModelsList.modelsListTable.columns.actions') }}</span>
                    </template>
                    <b-dropdown-item-button @click="goToEditingModel(item.id, 'update')">
                      <small>{{ $t('scoringProfilesModelsList.modelsListActions.update') }}</small>
                    </b-dropdown-item-button>
                    <b-dropdown-item-button @click="goToEditingModel(item.id, 'duplicate')">
                      <small>{{ $t('scoringProfilesModelsList.modelsListActions.duplicate') }}</small>
                    </b-dropdown-item-button>
                    <b-dropdown-item-button @click="goToDeleteModel(item.id)">
                      <small>{{ $t('scoringProfilesModelsList.modelsListActions.delete') }}</small>
                    </b-dropdown-item-button>
                  </b-dropdown>
                </div>
              </template>
            </b-table>
          </b-col>
        </b-row>
        <b-row class="mb-4" v-if="getPagination.items > getPagination.perPage">
          <b-col cols="12">
            <div class="float-right">
              <b-pagination
                class="mb-0"
                :value="getPagination.currentPage"
                :total-rows="getPagination.items"
                :per-page="getPagination.perPage"
                @change="onChangeCurrentPage"
              >
              </b-pagination>
            </div>
          </b-col>
        </b-row>
      </template>
      <template v-else>
        <no-content
          :icon-name="'exclamation-circle'"
          :icon-class="['mb-3', 'fa-3x']"
          :text="$t('scoringProfilesModelsList.noContent')"
        >
        </no-content>
      </template>
    </b-container>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import CustomSearchBar from '../../components/ScoringProfiles/CustomSearchBar.vue'
import TablePagination from '../../components/ScoringProfiles/TablePagination.vue'
import CustomFlexHeader from '../../components/Lib/CustomFlexHeader.vue'
import TableFilters from '../../components/Lib/TableFilters.vue'
import NoContent from '../../components/Lib/NoContent.vue'
const { mapActions, mapGetters, mapMutations } = createNamespacedHelpers('scoringProfilesModelsList')
import { formatDate } from '../../utils/formatter'
import { prepareModalList } from '../../utils/modals'

export default {
  components: {
    CustomFlexHeader,
    CustomSearchBar,
    TablePagination,
    TableFilters,
    NoContent,
  },

  data() {
    return {
      pagination_options: [25, 100, 500, 1000],
    }
  },
  computed: {
    ...mapGetters(['getPagination', 'getModelsList', 'getSearchBarValue', 'getLoading']),

    getPaginationItemsCounter: {
      get() {
        let counter = 1
        if (this.getPagination !== null && this.getPagination.items !== 0) {
          counter =
            this.getPagination.items <= this.getPagination.perPage
              ? 1
              : this.getPagination.perPage * (this.getPagination.currentPage - 1) + 1
        }

        return this.$t('scoringProfilesModelsList.shown', {
          start: counter,
          end: Math.min(this.getPagination.perPage * this.getPagination.currentPage, this.getPagination.items),
          of: this.getPagination.items || 0,
        })
      },
    },

    hasSearched() {
      return this.getSearchBarValue.length > 0
    },

    fields: {
      get() {
        return [
          {
            key: 'name',
            sortKey: 'Name',
            sortable: true,
            label: this.$t('scoringProfilesModelsList.modelsListTable.columns.name'),
            class: ['align-middle', 'font-weight-bold'],
          },
          {
            key: 'lastEditedBy',
            sortKey: 'User',
            sortable: true,
            label: this.$t('scoringProfilesModelsList.modelsListTable.columns.user'),
            class: ['align-middle'],
          },
          {
            key: 'createdAt',
            formatter: (value) => {
              return formatDate(value)
            },
            sortable: true,
            label: this.$t('scoringProfilesModelsList.modelsListTable.columns.creation'),
            class: ['align-middle'],
          },
          {
            key: 'lastEditedAt',
            formatter: (value) => {
              return formatDate(value)
            },
            sortable: true,
            label: this.$t('scoringProfilesModelsList.modelsListTable.columns.lastEditDate'),
            class: ['align-middle'],
          },
          {
            key: 'actions',
            label: this.$t('scoringProfilesModelsList.modelsListTable.columns.actions'),
            class: ['actions', 'align-middle'],
          },
        ]
      },
    },
  },
  methods: {
    ...mapMutations(['setSearchBarValue', 'setPaginationPerPage', 'setPaginationCurrentPage', 'setSortingValue']),
    ...mapActions(['loadModelsList', 'deleteModel', 'checkProfileLinking']),

    onResetFilters() {
      this.setSearchBarValue('')
      this.getPagination.currentPage === 1 ? this.loadModelsList() : this.setPaginationCurrentPage(1)
    },

    goToScoringProfiles() {
      this.$router.push({ name: 'scoringProfilesList' })
    },

    goToNewModel() {
      this.$router.push({ name: 'scoringProfilesModelCreate' })
    },

    goToEditingModel(id, action) {
      this.$store.dispatch('scoringProfilesModelsEditing/setIsDuplicate', action === 'duplicate')
      this.$store.dispatch('scoringProfilesModelsEditing/setScoringProfileModelId', id)
      this.$router.push({
        name: 'scoringProfilesModelEdit',
        params: { id: action === 'update' ? id.toString() : 'copy_' + id },
      })
    },

    goToDeleteModel(id) {
      this.checkProfileLinking(id).then((linkedModels) => {
        // No linked models
        if (Array.isArray(linkedModels) && linkedModels.length === 0) {
          this.deleteNoLinkedProfiles(id)
        } else {
          const message = prepareModalList(this.$t('scoringProfilesModelsList.modalMessages.noDelete'), linkedModels)
          const messageNodes = this.$createElement('div', { domProps: { innerHTML: message } })
          this.deleteLinkedProfiles(messageNodes)
        }
      })
    },

    deleteNoLinkedProfiles(id) {
      this.$bvModal
        .msgBoxConfirm(this.$t('scoringProfilesModelsList.modalMessages.confirmDelete'), {
          cancelTitle: this.$t('common.cancel'),
        })
        .then((value) => {
          if (value) {
            this.deleteModel(id).catch(async (err) => {
              const message = await err.response.text()
              // The error code arrives JSON-encoded (quoted), hence includes() rather than equality.
              if (message.includes('IsAssociatedToAtLeastOneScoringProfile')) {
                this.$bvToast.toast(this.$t('scoringProfilesModelsList.modalMessages.errorAssociatedToAProfile'), {
                  title: this.$t('scoringProfilesModelsList.scoringProfilesModelsTitle'),
                  variant: 'danger',
                })
              } else if (message.includes('ScoringProfileModelIsUsedByEfficiencyTests')) {
                // Not listed in advance like the scoring profiles: the linked-profiles lookup only
                // knows about profiles, so the backend refusal is the only signal.
                this.$bvToast.toast(
                  this.$t('scoringProfilesModelsList.modalMessages.errorAssociatedToAnEfficiencyTest'),
                  {
                    title: this.$t('scoringProfilesModelsList.scoringProfilesModelsTitle'),
                    variant: 'danger',
                  }
                )
              } else {
                this.$bvModal.msgBoxOk(this.$t('common.genericError'))
              }
            })
          }
        })
    },

    deleteLinkedProfiles(messageNodes) {
      this.$bvModal
        .msgBoxOk([messageNodes], { okVariant: 'secondary', okTitle: this.$t('common.cancel') })
        .then(() => {})
        .catch((err) => console.log(err))
    },

    onSearchModel(value) {
      this.setSearchBarValue(value)
      this.getPagination.currentPage === 1 ? this.loadModelsList() : this.setPaginationCurrentPage(1)
    },

    onChangeCurrentPage(value) {
      this.setPaginationCurrentPage(value)
      this.loadModelsList()
    },

    onChangePerPage(value) {
      this.setPaginationPerPage(value)
      if (this.getPagination.currentPage === 1) {
        this.loadModelsList()
      }
    },

    onSortingChanged(data) {
      let val = data.sortDesc ? '-' + data.sortBy : data.sortBy
      this.setSortingValue(val)
      this.getPagination.currentPage === 1 ? this.loadModelsList() : this.setPaginationCurrentPage(1)
    },
  },

  created() {
    this.loadModelsList().catch((err) => {
      this.httpErrorsHandler(err)
    })
  },
}
</script>

<style lang="scss">
.page-link {
  background-color: #e9ecef;
}

.reset-button {
  font-size: 0.8rem;
}

.button-primary-class,
.button-primary-class:active {
  border: 0;
  color: var(--secondaryColor) !important;
  background-color: transparent !important;
}

.show > .button-primary-class {
  color: var(--secondaryColor) !important;
}

.page-item.disabled .page-link {
  background-color: #e9ecef;
}

// Scoped to this table: the same selector in the other lists is global CSS, and whichever file loads
// last would otherwise decide the column widths of all of them.
table.list-table.models-list-table {
  th,
  td {
    &.actions {
      width: 3%;
    }
  }
}
</style>
